import React from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings, effectivePrice } from "@/context/SettingsContext";
import { resolvePhotoUrl } from "@/lib/api";
import { toast } from "sonner";

export function ProductCard({ product, index = 0 }) {
    const { addItem } = useCart();
    const { currency_symbol } = useSettings();
    const eff = effectivePrice(product);
    const hasDisc = Number(product.discount_percent || 0) > 0;

    const onAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addItem({ ...product, price: eff }, 1);
        toast.success(`${product.name} added to cart`);
    };

    return (
        <Link
            to={`/product/${product.id}`}
            data-testid={`product-card-${product.id}`}
            className="relative flex flex-col bg-surface/40 border border-border/50 rounded-3xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300 animate-fadeUp"
            style={{ animationDelay: `${index * 40}ms` }}
        >
            <div className="aspect-square w-full overflow-hidden bg-border/20 relative">
                {product.photo_url ? (
                    <img
                        src={resolvePhotoUrl(product.photo_url)}
                        alt={product.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                        No photo
                    </div>
                )}
                {hasDisc && (
                    <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-1 rounded-full shadow-sm">
                        -{product.discount_percent}%
                    </span>
                )}
            </div>
            <div className="p-4 flex flex-col gap-1 items-start">
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {product.category}
                </span>
                <h3 className="font-serif text-lg leading-tight text-foreground">
                    {product.name}
                </h3>
                <div className="mt-2 flex w-full items-center justify-between">
                    <span className="font-serif text-xl text-primary font-semibold">
                        {currency_symbol}{eff.toFixed(2)}
                        {hasDisc && (
                            <span className="ml-2 text-xs line-through text-muted-foreground font-sans">
                                {currency_symbol}{product.price.toFixed(2)}
                            </span>
                        )}
                    </span>
                    <button
                        onClick={onAdd}
                        data-testid={`add-to-cart-${product.id}`}
                        aria-label={`Add ${product.name} to cart`}
                        className="rounded-full bg-foreground text-background p-2 hover:bg-primary transition-colors active:scale-95"
                    >
                        <Plus className="h-4 w-4" strokeWidth={2} />
                    </button>
                </div>
            </div>
        </Link>
    );
}
